import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Wand2, Sparkles } from "lucide-react";
import { scrollToId } from "@/components/Navbar";
import { sfx } from "@/lib/sounds";

function GhostFloat({ className, delay = 0, scale = 1 }) {
  return (
    <motion.svg
      viewBox="0 0 64 64"
      width={54 * scale}
      className={`absolute pointer-events-auto cursor-pointer ${className}`}
      animate={{ y: [0, -18, 0], rotate: [-3, 3, -3] }}
      transition={{ duration: 5 + delay, repeat: Infinity, ease: "easeInOut" }}
      onClick={() => sfx.boo()}
      data-testid="hero-ghost"
    >
      <path
        d="M32 8c-11 0-17 9-17 20v24l7-6 5 6 5-6 5 6 5-6 7 6V28c0-11-6-20-17-20z"
        fill="rgba(255,247,238,0.9)"
      />
      <circle cx="26" cy="29" r="3" fill="#241531" />
      <circle cx="38" cy="29" r="3" fill="#241531" />
      <ellipse cx="32" cy="37" rx="3" ry="4" fill="#FF7A00" />
    </motion.svg>
  );
}

function BatSwarm() {
  const bats = [
    { top: "18%", dur: 26, delay: 0, size: 30 },
    { top: "32%", dur: 34, delay: 6, size: 22 },
    { top: "12%", dur: 40, delay: 14, size: 26 },
  ];
  return bats.map((b, i) => (
    <motion.svg
      key={i}
      viewBox="0 0 48 24"
      width={b.size}
      className="absolute left-0 pointer-events-none"
      style={{ top: b.top }}
      initial={{ x: "-10vw" }}
      animate={{ x: "110vw" }}
      transition={{ duration: b.dur, repeat: Infinity, delay: b.delay, ease: "linear" }}
    >
      <path
        d="M0 10 C 6 2 14 2 24 8 C 34 2 42 2 48 10 C 40 8 36 12 34 17 C 30 12 26 13 24 18 C 22 13 18 12 14 17 C 12 12 8 8 0 10 Z"
        fill="rgba(20,18,31,0.95)"
        stroke="rgba(255,122,0,0.5)"
        strokeWidth="1"
      />
    </motion.svg>
  ));
}

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { delay: 2.15 + i * 0.14, duration: 0.85, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const moonY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const ghostY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} data-testid="hero" className="relative flex min-h-[100svh] items-center overflow-hidden">
      <div className="absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-pumpkin/20 blur-[130px]" />
      <div className="absolute -right-24 bottom-0 h-96 w-96 rounded-full bg-lavender/20 blur-[130px]" />
      <div className="absolute left-1/3 bottom-10 h-64 w-64 rounded-full bg-slime/10 blur-[110px]" />

      <motion.div style={{ y: moonY }} className="absolute right-[6%] top-[10%] sm:right-[12%]">
        <div className="animate-flicker h-36 w-36 rounded-full bg-[#FFE8C2] shadow-[0_0_80px_30px_rgba(255,180,80,0.35)] sm:h-52 sm:w-52" />
      </motion.div>

      <motion.div style={{ y: ghostY }}>
        <GhostFloat className="left-[6%] top-[22%] hidden sm:block" delay={0.4} />
        <GhostFloat className="right-[26%] top-[16%] hidden lg:block" delay={1.2} scale={0.8} />
        <GhostFloat className="left-[14%] bottom-[14%] hidden md:block" delay={2} scale={0.7} />
      </motion.div>
      <BatSwarm />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-32 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.05 }}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-slime/40 bg-slime/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-slime"
        >
          <Sparkles size={14} /> The haunted coloring adventure
        </motion.div>

        <h1 className="font-display leading-[0.9]">
          <span className="block overflow-hidden">
            <motion.span
              custom={0}
              variants={line}
              initial="hidden"
              animate="show"
              className="block text-[clamp(4.5rem,14vw,11rem)] text-pumpkin text-glow-orange animate-flicker"
            >
              Boo
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span
              custom={1}
              variants={line}
              initial="hidden"
              animate="show"
              className="block text-[clamp(3.2rem,10vw,8rem)] text-paper"
            >
              Scribbles
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.5 }}
          className="mt-4 font-hand text-2xl text-slime text-glow-slime sm:text-3xl"
        >
          ~ coloring world ~
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.62 }}
          className="mt-6 max-w-xl text-base text-paper/75 sm:text-lg"
        >
          Tap to flood friendly ghouls with glowing colors, scribble with a magic
          brush, earn stickers and unlock secret pages hidden in the dark...
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.75 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-start-btn"
            onClick={() => {
              sfx.boop();
              scrollToId("vault");
            }}
            className="group flex items-center gap-2 rounded-full bg-pumpkin px-8 py-4 text-lg font-bold text-ink shadow-glow-orange transition-transform hover:scale-105 active:scale-95"
          >
            <Wand2 size={20} className="transition-transform group-hover:rotate-12" />
            Start Coloring
          </button>
          <button
            data-testid="hero-secrets-btn"
            onClick={() => {
              sfx.click();
              scrollToId("stickers");
            }}
            className="rounded-full border-2 border-slime/60 px-8 py-4 text-lg font-semibold text-slime transition-all hover:bg-slime/10 hover:shadow-glow-slime"
          >
            Unlock Secret Pages
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3 }}
          className="mt-12 flex flex-wrap gap-3 text-sm font-medium text-paper/70"
        >
          {["14 magical pages", "10 sticker rewards", "4 secret categories"].map((t) => (
            <span key={t} className="rounded-full border border-white/15 bg-white/5 px-4 py-2">
              {t}
            </span>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3.2 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2"
      >
        <div className="animate-floaty flex h-10 w-6 items-start justify-center rounded-full border-2 border-white/30 p-1">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.6, repeat: Infinity }}
            className="h-2 w-2 rounded-full bg-pumpkin"
          />
        </div>
      </motion.div>
    </section>
  );
}
